/**
 * SelectableOptionCard
 *
 * A radio-style selectable card: a bordered panel with a radio "dot" affordance
 * on the left and a title + description on the right. Selecting one card in a
 * group is a deliberate, reversible choice — the parent only acts once the
 * clinician presses a separate primary CTA. This "select then confirm" pattern
 * (rather than "click an option and immediately act") guards against a single
 * mis-click committing the user to an outcome.
 *
 * Shared so every multi-choice completion dialog looks and behaves identically:
 *   - CIFinishPathDialog  (Clinical Interview finish paths + PAD +ve/−ve)
 *   - PadRedoDialog       (Redo vs Leave-as-is after a DD mismatch)
 *
 * Wrap a set of these in a container with role="radiogroup" for accessibility.
 */

import React from 'react';

/**
 * @param {Object} props
 * @param {boolean} props.selected     Whether this card is the chosen option.
 * @param {() => void} props.onSelect   Called when the card is clicked.
 * @param {React.ReactNode} props.title Bold heading line.
 * @param {React.ReactNode} props.description Supporting copy under the title.
 * @param {boolean} [props.disabled=false] Greys out and blocks selection.
 */
export default function SelectableOptionCard({
  selected,
  onSelect,
  title,
  description,
  disabled = false,
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      onClick={onSelect}
      className={`w-full text-left rounded-lg border p-4 transition group ${
        selected
          ? 'border-violet-600 bg-violet-50 ring-2 ring-violet-300'
          : 'border-gray-200 bg-white hover:border-violet-300 hover:bg-violet-50/40'
      } ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`
            flex-shrink-0 mt-0.5 w-4 h-4 rounded-full border-2
            transition-all duration-200 ease-in-out
            flex items-center justify-center
            ${selected
              ? 'border-violet-700 bg-violet-700'
              : 'border-gray-300 group-hover:border-violet-400'
            }
          `}
          aria-hidden="true"
        >
          {selected && (
            <div className="w-2 h-2 bg-white rounded transition-all duration-200 ease-in-out" />
          )}
        </div>
        <div className="min-w-0">
          <p className={`font-semibold ${selected ? 'text-violet-900' : 'text-gray-900'}`}>
            {title}
          </p>
          <p className="text-sm text-gray-600 mt-1">{description}</p>
        </div>
      </div>
    </button>
  );
}
